import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import MobileVerifyGate from "@/components/MobileVerifyGate";
import DashboardAdvisor from "@/components/DashboardAdvisor";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Plus, FileStack, MapPin, Layers, Loader2, Building2, ArrowRight,
  Gauge, IndianRupee, TrendingDown, CalendarClock, Sparkles, Download, ShieldCheck
} from "lucide-react";

const compactINR = (n) => {
  if (!n) return "₹0";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [myPlans, setMyPlans] = useState([]);
  const [crn, setCrn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdvisor, setShowAdvisor] = useState(false);

  const load = async () => {
    try {
      const [pr, co, mp] = await Promise.all([api.get("/projects"), api.get("/consultations"), api.get("/plans/my")]);
      setProjects(pr.data);
      setConsultations(co.data);
      setMyPlans(mp.data.plans || []);
      setCrn(mp.data.crn);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // After signing in, honor an intended destination (e.g. Create My Project from the homepage).
  useEffect(() => {
    if (loading) return;
    const goto = localStorage.getItem("bg_goto");
    if (goto) { localStorage.removeItem("bg_goto"); navigate(goto); return; }
    if (localStorage.getItem("bg_chat_continue")) {
      localStorage.removeItem("bg_chat_continue");
      if (projects.length > 0) navigate(`/project/${projects[0].id}?tab=chat`);
      else setShowAdvisor(true);
    } else if (projects.length === 0) {
      setShowAdvisor(true);
    }
  }, [loading, projects, navigate]);

  const downloadInvoice = async (txnId) => {
    try {
      const res = await api.get(`/plans/invoice/${txnId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `invoice_${txnId.slice(-6)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Invoice not available"); }
  };

  const reported = projects.filter((p) => p.report);
  const avgScore = reported.length ? Math.round(reported.reduce((s, p) => s + (p.report.overall_score || 0), 0) / reported.length) : null;
  const totalCost = reported.reduce((s, p) => s + (p.report?.estimated_cost?.total_inr || 0), 0);
  const totalSavings = reported.reduce((s, p) => s + (p.report?.optimizations || []).reduce((a, o) => a + (o.potential_saving_inr || 0), 0), 0);
  const totalDocs = projects.reduce((s, p) => s + (p.doc_count || 0), 0);

  const kpis = [
    { icon: Building2, label: "Active Projects", value: projects.length, tint: "text-slate-900" },
    { icon: Gauge, label: "Avg Health Score", value: avgScore !== null ? `${avgScore}/100` : "—", tint: "text-[#0E9AC8]" },
    { icon: IndianRupee, label: "Est. Construction Cost", value: compactINR(totalCost), tint: "text-slate-900" },
    { icon: TrendingDown, label: "Potential Optimisation", value: compactINR(totalSavings), tint: "text-emerald-600" },
    { icon: FileStack, label: "Documents", value: totalDocs, tint: "text-slate-900" },
    { icon: CalendarClock, label: "Consultations", value: consultations.filter((c) => c.status === "paid_confirmed").length, tint: "text-slate-900" },
  ];

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <Navbar />
      <MobileVerifyGate />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-sm text-slate-500">Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</p>
            <h1 className="mt-1 font-display text-3xl lg:text-4xl tracking-tighter font-extrabold text-slate-900">Your Construction Dashboard</h1>
            {crn && (
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="crn-badge">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-slate-900 text-white px-3 py-1">CRN: {crn}</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-[#0E9AC8]/10 text-[#0E9AC8] px-3 py-1"><Sparkles className="h-3 w-3" /> AI Starter Member</span>
              </div>
            )}
          </div>
          <Button data-testid="new-project-button" onClick={() => navigate("/new-project")}
            className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11 px-6 shadow-sm transition-colors">
            <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> New Project
          </Button>
        </div>

        {/* Recommended next step / welcome-back band */}
        {!loading && projects.length > 0 && (
          <div className="mb-10 rounded-2xl border border-[#0E9AC8]/25 bg-gradient-to-br from-[#0B1B2B] to-[#0e2c4d] text-white p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="recommended-next-step">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-[#0E9AC8]/20 flex items-center justify-center shrink-0">
                <Sparkles className="h-5 w-5 text-[#7dd3fc]" strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#7dd3fc]">Recommended next step</p>
                <p className="mt-1.5 text-sm sm:text-base text-white/85 leading-relaxed max-w-2xl">
                  {projects[0].report
                    ? `Ask NCIS™ about ${projects[0].name}, or find verified vendors for your next task.`
                    : `Continue with ${projects[0].name} — generate its Construction Health Report to unlock cost, risks and savings.`}
                </p>
              </div>
            </div>
            <Button data-testid="recommended-cta" onClick={() => navigate(projects[0].report ? `/project/${projects[0].id}?tab=chat` : `/project/${projects[0].id}?tab=report`)}
              className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11 px-6 shrink-0 transition-colors">
              {projects[0].report ? "Ask NCIS™" : "Continue"} <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
            </Button>
          </div>
        )}

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          {kpis.map((k) => (
            <div key={k.label} data-testid={`kpi-${k.label}`} className="card-premium rounded-2xl p-5">
              <div className="h-9 w-9 rounded-lg bg-[#0E9AC8]/10 flex items-center justify-center mb-3">
                <k.icon className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.6} />
              </div>
              <p className={`font-display text-2xl font-extrabold tracking-tight ${k.tint}`}>{k.value}</p>
              <p className="mt-1 text-xs text-slate-500">{k.label}</p>
            </div>
          ))}
        </div>

        {showAdvisor && (
          <div className="mb-10" data-testid="advisor-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold text-slate-900">AI Construction Advisor</h2>
              {projects.length > 0 && (
                <Button variant="outline" onClick={() => navigate(`/project/${projects[0].id}?tab=chat`)} className="rounded-xl text-sm border-slate-300 hover:border-[#0E9AC8] hover:text-[#0E9AC8]">
                  Open advisor for {projects[0].name} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              )}
            </div>
            <DashboardAdvisor userId={user?.user_id} />
            {projects.length === 0 && (
              <p className="mt-3 text-sm text-slate-500">Tip: create a project below to unlock document analysis, cost estimates and your Construction Health Report.</p>
            )}
          </div>
        )}

        <div id="plans" className="mb-10" data-testid="my-plans">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-xl font-bold text-slate-900">My Plans</h2>
            <Button variant="outline" onClick={() => navigate("/plans")} className="rounded-xl text-sm border-slate-300 hover:border-[#0E9AC8] hover:text-[#0E9AC8]">Explore Plans</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {myPlans.map((pl) => (
              <div key={pl.id} data-testid={`myplan-${pl.id}`} className={`card-premium rounded-2xl p-6 ${pl.is_membership ? "border-[#0E9AC8]/40" : ""}`}>
                <div className="flex items-start justify-between">
                  <h3 className="font-display text-lg font-extrabold text-slate-900 pr-2">{pl.plan_name}</h3>
                  <span className={`shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${pl.expired ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>{pl.expired ? "Expired" : "Active"}</span>
                </div>
                {pl.is_membership ? (
                  <p className="mt-2 text-sm text-slate-500 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[#0E9AC8]" /> Free membership · CRN {pl.crn}</p>
                ) : (
                  <>
                    <p className="mt-2 text-sm text-slate-500">{compactINR(pl.amount)} · {pl.invoice_no}</p>
                    <p className="mt-1 text-xs text-slate-400">{pl.expiry_at ? `Valid till ${new Date(pl.expiry_at).toLocaleDateString("en-IN")}` : "One-time"}</p>
                  </>
                )}
                {(pl.addons || []).length > 0 && <p className="mt-2 text-xs text-slate-500">Add-ons: {pl.addons.join(", ")}</p>}
                <div className="mt-4 flex gap-2">
                  {!pl.is_membership && (
                    <Button size="sm" variant="outline" onClick={() => downloadInvoice(pl.txn_id)} className="rounded-xl text-xs">
                      <Download className="h-3.5 w-3.5 mr-1" /> Invoice
                    </Button>
                  )}
                  {pl.expired && (
                    <Button size="sm" onClick={() => navigate("/plans")} className="rounded-xl text-xs bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white">Renew</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold text-slate-900">Projects</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div>
        ) : projects.length === 0 ? (
          <div className="card-premium rounded-2xl p-16 text-center" data-testid="empty-state">
            <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-4" strokeWidth={1.25} />
            <h3 className="font-display text-xl font-bold text-slate-900">No projects yet</h3>
            <p className="mt-2 text-slate-500">Create your first project to get your Construction Health Report.</p>
            <Button onClick={() => navigate("/new-project")} className="mt-6 bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl transition-colors">
              <Plus className="mr-2 h-4 w-4" /> Create Project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p) => (
              <button key={p.id} data-testid={`project-card-${p.id}`} onClick={() => navigate(`/project/${p.id}`)}
                className="text-left card-premium rounded-2xl p-6 hover:-translate-y-1 transition-transform duration-200 group">
                <div className="flex items-start justify-between">
                  <h3 className="font-display text-xl font-bold text-slate-900 pr-4">{p.name}</h3>
                  {p.report ? (
                    <span className="shrink-0 text-xs font-bold px-2.5 py-1 rounded-full bg-[#0E9AC8]/10 text-[#0E9AC8]">{p.report.overall_score}/100</span>
                  ) : (
                    <span className="shrink-0 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">No report</span>
                  )}
                </div>
                <div className="mt-4 space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" strokeWidth={1.5} /> {p.city}</div>
                  <div className="flex items-center gap-2"><Layers className="h-4 w-4 text-slate-400" strokeWidth={1.5} /> {p.plot_area} {p.plot_unit} · {p.floors} floor(s)</div>
                  {p.est_construction_area ? (
                    <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" strokeWidth={1.5} /> {p.est_construction_area.toLocaleString("en-IN")} sq ft est. area</div>
                  ) : p.budget ? (
                    <div className="flex items-center gap-2"><IndianRupee className="h-4 w-4 text-slate-400" strokeWidth={1.5} /> {compactINR(p.budget)} budget</div>
                  ) : null}
                  <div className="flex items-center gap-2"><FileStack className="h-4 w-4 text-slate-400" strokeWidth={1.5} /> {p.doc_count} document(s)</div>
                </div>
                <div className="mt-5 flex items-center text-sm font-semibold text-[#0E9AC8] group-hover:gap-2 transition-all">
                  Open project <ArrowRight className="ml-1 h-4 w-4" strokeWidth={2} />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
